import type {
    CalendarViewOptions,
    DataSourceAbstract,
    FieldType,
    GalleryViewOptions,
    GroupFieldValue,
    ViewBlockAbstract,
    ViewLayout,
    ViewType
} from '@lighthouse/core'
import {
    ALIGNMENT,
    ARRANGEMENT,
    BACKGROUND_TYPE,
    BLOCK_SIZE,
    BlockType,
    CUSTOM_VIEW_LAYOUT,
    DIRECTION,
    EDGE_MODE,
    EMPTY_VIEW_IMAGE_MODE,
    FLEX_ALIGN,
    GALLERY_AVATAR_SHAPE,
    GALLERY_AVATAR_SIZE,
    POSITION
} from '@lighthouse/core'
import { generateJson, getIsAppointField, NO_EDIT_SYSTEM_FIELD, USER_DATASOURCE, VIEW_BLOCK_NAME_MAP } from '@lighthouse/shared'

/**
 * 生成画廊block的配置
 * @date 2024/4/19 - 22:14:08
 *
 * @param {(undefined | DataSourceAbstract)} dataSource
 * @returns {GalleryViewOptions}
 */
export const generateGalleryConfig = (dataSource: undefined | DataSourceAbstract): GalleryViewOptions => {
    if (!dataSource) {
        return {}
    }

    const fields = Object.values(dataSource.schema)

    // 用户表
    if (dataSource.id === USER_DATASOURCE) {
        const contentFields: GroupFieldValue[] = [
            {
                wrap: true,
                id: 'MOBILE'
            },
            {
                wrap: true,
                id: 'ROLE'
            }
        ]

        return {
            contentTags: {
                fields: contentFields,
                show: true,
                showLabel: false
            },
            footTags: {
                fields: [],
                show: false,
                showLabel: false
            },
            headAvatar: {
                align: ALIGNMENT.left,
                position: POSITION.top,
                shape: GALLERY_AVATAR_SHAPE.circle,
                show: false,
                size: GALLERY_AVATAR_SIZE.md
            },
            headCover: {
                field: 'AVATAR',
                objectFit: 'cover',
                ratio: '4:3',
                show: true
            },
            headTags: {
                fields: [],
                show: false,
                showLabel: false
            },
            headTitle: {
                field: 'USERNAME',
                show: true
            }
        }
    }

    // 普通表
    const coverField = fields.find(item => item.type === 'file')
    const textTypeSet = new Set<FieldType>(['text', 'phoneNumber', 'url', 'email'])
    const titleField = fields.find(item => textTypeSet.has(item.type))
    const contentFields = fields
        .reduce<GroupFieldValue[]>((total, current) => {
            if (current.id === 'ID') {
                return total
            }

            return [...total, { id: current.id, wrap: true }]
        }, [])
        .slice(0, 4)

    return {
        contentTags: {
            fields: contentFields,
            show: true,
            showLabel: false
        },
        footTags: {
            fields: [],
            show: false,
            showLabel: false
        },
        headAvatar: {
            align: ALIGNMENT.left,
            position: POSITION.top,
            shape: GALLERY_AVATAR_SHAPE.circle,
            show: false,
            size: GALLERY_AVATAR_SIZE.md
        },
        headCover: {
            field: coverField?.id,
            objectFit: 'cover',
            ratio: '4:3',
            show: true
        },
        headTags: {
            fields: [],
            show: false,
            showLabel: true
        },
        headTitle: {
            field: titleField?.id,
            show: true
        }
    }
}

/**
 * 生成日历block的配置
 *
 * @param {DataSourceAbstract} dataSource
 * @returns {CalendarViewOptions}
 */
export const generateCalendarConfig = (dataSource: undefined | DataSourceAbstract): CalendarViewOptions => {
    if (!dataSource) {
        return {}
    }

    const fields = Object.values(dataSource.schema)
    const title = fields.find(item => item.type === 'text')
    const startDate = fields.find(item => item.type === 'date' && !getIsAppointField(item.id, NO_EDIT_SYSTEM_FIELD))
    const endDate =
        startDate &&
        fields.find(item => item.type === 'date' && !getIsAppointField(item.id, NO_EDIT_SYSTEM_FIELD) && item.id !== startDate.id)

    return {
        scheduleTitleField: title?.id,
        scheduleStartDateField: startDate?.id,
        scheduleEndDateField: endDate?.id
    }
}

/**
 * 生成自定义视图block的配置
 *
 * @returns {ViewLayout}
 */
const generateCustomLayout: ViewLayout = {
    direction: 'vertical',
    wrap: false,
    layout: CUSTOM_VIEW_LAYOUT.grid,
    arrangement: ARRANGEMENT.auto,
    size: BLOCK_SIZE.md,
    gap: {
        size: 8,
        unit: 'px'
    },
    viewPadding: { size: [4, 4, 4, 4], unit: 'px' }
}

const generateGalleryLayout: ViewLayout = {
    arrangement: ARRANGEMENT.auto,
    direction: DIRECTION.vertical,
    size: BLOCK_SIZE.md
}

type Params = {
    viewType: ViewType
    id: string
    appId?: string
    dataSource?: DataSourceAbstract
}

/**
 * 生成视图block
 *
 * @param {Params} param0
 * @param {ViewType} param0.viewType
 * @param {string} param0.id
 * @param {string} [param0.appId='']
 * @param {DataSourceAbstract} param0.dataSource
 * @returns {(undefined | ViewBlockAbstract)}
 */
export const generateViewBlock = (
    mainBreakPoint: string,
    { viewType, id, appId = '', dataSource }: Params
): undefined | ViewBlockAbstract => {
    return {
        id,
        type: BlockType.view,
        title: VIEW_BLOCK_NAME_MAP[viewType],
        config: {
            pointer: '',
            showTitle: false,
            canSearch: false,
            canEdit: false,
            canDisplay: false,
            required: false,
            canSort: false,

            // 系统表需要禁用,先统一关闭、使用后端返回的值
            groupConfigure: {
                groupByFieldId: '',
                groupConfig: []
            },
            toolbar: [],
            recordOperator: [],
            viewFieldSettings: [],
            canPaginate: true,
            eventList: [],
            linkFilterController: {
                expression: {
                    where: 'AND',
                    conditions: []
                }
            },
            pagination: {
                pageSize: 10
            },
            quickFilter: { mode: 'normal', fieldIds: [] },
            canSetKanban: true,
            canSortKanban: true,
            viewType,
            appId,
            breakPoint:
                viewType === 'custom'
                    ? {
                          id: mainBreakPoint,
                          visibility: {
                              visible: 'VISIBLE'
                          },
                          position: {
                              type: 'relative'
                          },
                          size: {
                              width: {
                                  size: 'fill'
                              },
                              height: {
                                  size: 'auto'
                              },
                              overflow: 'visible'
                          },
                          viewLayout: generateCustomLayout,
                          viewStyles: {
                              calendarDateRow: {
                                  weekColor: {
                                      type: 'color',
                                      color: '#101828'
                                  },
                                  dateColor: {
                                      type: 'color',
                                      color: '#98A2B3'
                                  }
                              },
                              calendarTimeCol: {
                                  timeColor: {
                                      type: 'color',
                                      color: '#101828'
                                  }
                              },
                              showGrid: true,
                              gridColor: {
                                  type: 'color',
                                  color: '#E4E7EC'
                              }
                          },
                          layout: {
                              align: {
                                  direction: DIRECTION.vertical,
                                  alignX: FLEX_ALIGN['flex-start'],
                                  alignY: FLEX_ALIGN['flex-start']
                              },
                              gap: {
                                  size: 8,
                                  unit: 'px'
                              },
                              padding: { size: [4, 4, 4, 4], unit: 'px' }
                          },
                          design: {
                              radius: [8, 8, 8, 8],
                              border: {
                                  type: BACKGROUND_TYPE.color,
                                  color: '#e4e7ec',
                                  mode: EDGE_MODE.each,
                                  each: [1, 1, 1, 1]
                              }
                          },
                          emptyView: {
                              title: generateJson('找不到数据'),
                              imageMode: EMPTY_VIEW_IMAGE_MODE.default
                          }
                      }
                    : {
                          id: mainBreakPoint,
                          visibility: {
                              visible: 'VISIBLE'
                          },
                          position: {
                              type: 'relative'
                          },
                          size: {
                              width: {
                                  size: 'fill'
                              },
                              height: {
                                  size: 'auto'
                              },
                              overflow: 'visible'
                          },
                          viewLayout: generateGalleryLayout,
                          layout: {},
                          design: {},
                          emptyView: {
                              title: generateJson('找不到数据'),
                              imageMode: EMPTY_VIEW_IMAGE_MODE.default
                          }
                      },

            breakPoints: []

            // ...generateGalleryConfig(dataSource),
            // ...generateCalendarConfig(dataSource),
        },
        children: []
    }
}
